import { useState } from "react";
import { motion } from "framer-motion";
import { Check, ArrowUpRight } from "lucide-react";

const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  transition: { duration: 0.6 },
  viewport: { once: true },
};

const categories = ["Tutti", "Social Media", "Content", "Reputation", "SEO", "Advertising", "PR", "Web"];

const galleryItems = [
  { title: "Facebook Marketing", cat: "Social Media", img: "https://images.pexels.com/photos/607812/pexels-photo-607812.jpeg?auto=compress&cs=tinysrgb&w=600" },
  { title: "Instagram Marketing", cat: "Social Media", img: "https://images.pexels.com/photos/106344/pexels-photo-106344.jpeg?auto=compress&cs=tinysrgb&w=600" },
  { title: "WhatsApp Business", cat: "Social Media", img: "https://images.pexels.com/photos/46924/pexels-photo-46924.jpeg?auto=compress&cs=tinysrgb&w=600" },
  { title: "Video Production", cat: "Content", img: "https://images.pexels.com/photos/2608517/pexels-photo-2608517.jpeg?auto=compress&cs=tinysrgb&w=600" },
  { title: "Graphic Design", cat: "Content", img: "https://images.pexels.com/photos/196644/pexels-photo-196644.jpeg?auto=compress&cs=tinysrgb&w=600" },
  { title: "Google My Business", cat: "Reputation", img: "https://images.pexels.com/photos/270637/pexels-photo-270637.jpeg?auto=compress&cs=tinysrgb&w=600" },
  { title: "Trustpilot", cat: "Reputation", img: "https://images.pexels.com/photos/3184465/pexels-photo-3184465.jpeg?auto=compress&cs=tinysrgb&w=600" },
  { title: "SEO & Analytics", cat: "SEO", img: "https://images.pexels.com/photos/97080/pexels-photo-97080.jpeg?auto=compress&cs=tinysrgb&w=600" },
  { title: "Google Ads", cat: "Advertising", img: "https://images.pexels.com/photos/3945313/pexels-photo-3945313.jpeg?auto=compress&cs=tinysrgb&w=600" },
  { title: "Digital Journalism", cat: "PR", img: "https://images.pexels.com/photos/518543/pexels-photo-518543.jpeg?auto=compress&cs=tinysrgb&w=600" },
  { title: "Web Design", cat: "Web", img: "https://images.pexels.com/photos/1779487/pexels-photo-1779487.jpeg?auto=compress&cs=tinysrgb&w=600" },
  { title: "AutoScout24", cat: "Reputation", img: "https://images.pexels.com/photos/373543/pexels-photo-373543.jpeg?auto=compress&cs=tinysrgb&w=600" },
];

const pricingPlans = [
  {
    name: "Starter",
    price: "350",
    desc: "Perfetto per iniziare la tua presenza digitale",
    highlight: "8 Post al Mese",
    features: [
      "8 post al mese sui social",
      "Costruzione contenuti grafici",
      "Piano editoriale mensile",
      "Gestione 1 profilo social",
      "Report mensile",
      "Supporto via email",
    ],
    featured: false,
  },
  {
    name: "Professional",
    price: "500",
    desc: "La soluzione completa per il tuo marketing digitale",
    highlight: "12 Post al Mese",
    features: [
      "12 post al mese sui social",
      "Costruzione contenuti grafici avanzati",
      "Piano editoriale strategico",
      "1 profilo gestito",
      "Stories e Reels inclusi",
      "SEO sito internet",
      "Google & Facebook Recensioni",
      "Supporto prioritario",
    ],
    featured: true,
  },
  {
    name: "Enterprise",
    price: "900",
    desc: "Marketing integrato a 360 gradi con massima visibilita",
    highlight: "24 Post al Mese",
    features: [
      "24 post al mese sui social",
      "Costruzione contenuti grafici premium",
      "Gestione completa social + WhatsApp",
      "Campagne Google Ads",
      "Reputation management avanzato",
      "Articoli giornalistici digitali",
      "Consulenza strategica dedicata",
      "Report analytics settimanale",
    ],
    featured: false,
  },
];

export default function GalleryPricingSection() {
  const [activeCat, setActiveCat] = useState("Tutti");

  const filtered = activeCat === "Tutti" ? galleryItems : galleryItems.filter((i) => i.cat === activeCat);

  return (
    <>
      {/* GALLERY */}
      <section data-testid="gallery-section" className="py-24 sm:py-32 bg-[#0a0a0a] border-y border-white/5">
        <div className="max-w-[1440px] mx-auto px-6 sm:px-12">
          <motion.div {...fadeInUp}>
            <span className="font-mono text-xs tracking-[0.2em] text-[#FF3366] uppercase">LA NOSTRA GALLERIA</span>
            <h2 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight mt-4">
              I nostri servizi <span className="text-[#A3A3A3]">in immagini</span>
            </h2>
          </motion.div>

          <motion.div {...fadeInUp} className="mt-10 flex flex-wrap gap-2">
            {categories.map((cat) => (
              <button
                key={cat}
                data-testid={`gallery-filter-${cat.toLowerCase().replace(/\s/g, '-')}`}
                onClick={() => setActiveCat(cat)}
                className={`font-mono text-xs tracking-[0.15em] px-4 py-2 transition-colors ${
                  activeCat === cat
                    ? "bg-[#FF3366] text-white"
                    : "border border-white/20 text-white/60 hover:text-white hover:border-white/40"
                }`}
              >
                {cat.toUpperCase()}
              </button>
            ))}
          </motion.div>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filtered.map((item, i) => (
              <motion.div
                key={item.title}
                data-testid={`gallery-item-${i}`}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                viewport={{ once: true }}
                className="gallery-item group relative border border-white/10 bg-[#121212]"
              >
                <div className="relative h-52 overflow-hidden">
                  <img src={item.img} alt={item.title} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#121212] via-transparent to-transparent opacity-80" />
                </div>
                <div className="p-4">
                  <span className="font-mono text-[10px] tracking-[0.2em] text-[#FF3366]">{item.cat.toUpperCase()}</span>
                  <h4 className="font-heading font-bold mt-1">{item.title}</h4>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="prezzi" data-testid="pricing-section" className="py-16 sm:py-24 lg:py-32 bg-[#050505]">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12">
          <motion.div {...fadeInUp} className="text-center">
            <span className="font-mono text-xs tracking-[0.2em] text-[#FF3366] uppercase">PREZZI</span>
            <h2 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight mt-4">
              Pacchetti <span className="text-[#A3A3A3]">su misura</span>
            </h2>
            <p className="text-[#A3A3A3] font-body text-lg mt-4 max-w-xl mx-auto">
              Scegli il pacchetto perfetto per le tue esigenze. Ogni piano e personalizzabile.
            </p>
          </motion.div>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
            {pricingPlans.map((plan, i) => (
              <motion.div
                key={i}
                data-testid={`pricing-card-${i}`}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.15 }}
                viewport={{ once: true }}
                className={`pricing-card border bg-[#121212] p-8 ${
                  plan.featured
                    ? "border-[#FF3366] relative"
                    : "border-white/10"
                }`}
              >
                {plan.featured && (
                  <div className="absolute -top-px left-0 right-0 h-1 bg-[#FF3366]" />
                )}
                {plan.featured && (
                  <span className="inline-block bg-[#FF3366] text-white text-xs font-mono tracking-wider px-3 py-1 mb-4">
                    CONSIGLIATO
                  </span>
                )}
                <h3 className="font-heading text-2xl font-bold">{plan.name}</h3>
                <p className="text-[#A3A3A3] text-sm mt-2 font-body">{plan.desc}</p>
                <div className="mt-4 inline-block bg-white/5 border border-white/10 px-3 py-1">
                  <span className="font-mono text-xs tracking-wider text-[#FF3366] font-bold">{plan.highlight}</span>
                </div>
                <div className="mt-4 flex items-baseline gap-1">
                  <span className="font-heading text-5xl font-black">{plan.price}</span>
                  <span className="text-[#A3A3A3] font-mono text-sm">&euro;/mese</span>
                </div>
                <p className="text-[#A3A3A3] text-xs font-mono mt-1">IVA esclusa</p>

                <ul className="mt-8 space-y-3">
                  {plan.features.map((f, j) => (
                    <li key={j} className="flex items-start gap-3 text-sm">
                      <Check size={16} className="text-[#FF3366] mt-0.5 flex-shrink-0" />
                      <span className="text-white/80">{f}</span>
                    </li>
                  ))}
                </ul>

                <a
                  href="#contatti"
                  data-testid={`pricing-cta-${i}`}
                  className={`mt-8 w-full flex items-center justify-center gap-2 font-bold uppercase tracking-widest px-6 py-4 text-sm transition-colors duration-300 ${
                    plan.featured
                      ? "bg-[#FF3366] text-white hover:bg-white hover:text-black"
                      : "border border-white/20 text-white hover:bg-white hover:text-black"
                  }`}
                >
                  RICHIEDI OFFERTA <ArrowUpRight size={16} />
                </a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
